import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/authApi';
import styles from '../css/Register.module.css';

const getTokenExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
};

// Страница /auth/register — выбор никнейма для нового пользователя
const AuthRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [login, setLogin] = useState('');
  const [loginStatus, setLoginStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const checkTimeout = useRef(null);
  const pendingToken = searchParams.get('pending_token');

  useEffect(() => {
    if (!pendingToken) {
      navigate('/auth/login', { replace: true });
      return;
    }
    const exp = getTokenExp(pendingToken);
    if (!exp) {
      navigate('/auth/login', { replace: true });
      return;
    }
    const remaining = exp - Date.now();
    if (remaining <= 0) {
      navigate('/auth/login', { replace: true });
      return;
    }
    const timer = setTimeout(() => {
      navigate('/auth/login', { replace: true });
    }, remaining);
    return () => clearTimeout(timer);
  }, [pendingToken, navigate]);

  const validateFormat = (value) => {
    if (value.length < 3 || value.length > 30) return false;
    return /^[a-zA-Z0-9_]+$/.test(value);
  };

  const handleLoginChange = useCallback((e) => {
    const value = e.target.value;
    setLogin(value);
    setSubmitError('');
    if (checkTimeout.current) clearTimeout(checkTimeout.current);
    if (!value) { setLoginStatus(null); return; }
    if (!validateFormat(value)) { setLoginStatus('invalid'); return; }
    setLoginStatus('checking');
    checkTimeout.current = setTimeout(async () => {
      try {
        const res = await authAPI.checkLogin(value);
        setLoginStatus(res.data.data.available ? 'available' : 'taken');
      } catch {
        setLoginStatus(null);
      }
    }, 400);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loginStatus !== 'available') return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await authAPI.completeRegister(pendingToken, login);
      const { access_token } = res.data.data;
      navigate(`/auth/login?access_token=${access_token}`, { replace: true });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        navigate('/auth/login', { replace: true });
      } else if (status === 409) {
        setLoginStatus('taken');
        setSubmitError('Этот никнейм уже занят.');
      } else {
        setSubmitError('Произошла ошибка. Попробуйте ещё раз.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hintMap = {
    checking:  { text: 'Проверяем...',                          cls: styles.hintChecking },
    available: { text: '✓ Никнейм свободен',                    cls: styles.hintAvailable },
    taken:     { text: '✗ Никнейм занят',                       cls: styles.hintTaken },
    invalid:   { text: 'Только буквы, цифры и _ (3–30 символов)', cls: styles.hintInvalid },
  };
  const hint = hintMap[loginStatus] || null;

  const inputCls = [
    styles.input,
    loginStatus === 'available' ? styles.inputValid : '',
    loginStatus === 'taken' || loginStatus === 'invalid' ? styles.inputError : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={styles.registerPage}>
      <div className={styles.registerCard}>
        <div className={styles.logoBlock}>
          <span className={styles.logoText}>DoramaHype</span>
        </div>

        <h1 className={styles.title}>Регистрация</h1>
        <p className={styles.subtitle}>Придумайте уникальный никнейм</p>

        <form className={styles.registerForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="login">Никнейм</label>
            <input
              id="login"
              type="text"
              className={inputCls}
              placeholder="например, dorama_fan_42"
              value={login}
              onChange={handleLoginChange}
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
            />
            {hint && <span className={hint.cls}>{hint.text}</span>}
          </div>

          {submitError && (
            <div className={styles.errorMessage}>{submitError}</div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loginStatus !== 'available' || submitting}
          >
            {submitting ? 'Регистрация...' : 'Зарегистрироваться'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AuthRegister;
